import { Config } from 'config';

import { getAuthToken } from './auth';

const AUTH_API_URL = String(Config.get('AUTH_API_URL') ?? '');
const REQUEST_TIMEOUT_MS = 8000;

/**
 * Machine-readable code returned by auth-api when the legacy Solar Insights
 * platform has been decommissioned for a (non-internal) customer. Keep in sync
 * with `legacy_decommission.LEGACY_DECOMMISSIONED_CODE` in the auth-api repo.
 */
export const LEGACY_DECOMMISSIONED_CODE = 'legacy_decommissioned';

export const DEFAULT_DECOMMISSION_TITLE = 'This Legacy platform has been retired.';

export const DEFAULT_DECOMMISSION_MESSAGE = 'Please log in to the new Asset Insights platform to continue.';

export const ASSET_INSIGHTS_BUTTON_LABEL = 'Go to Asset Insights';

const DEFAULT_ASSET_INSIGHTS_URL = 'https://insights.zeitview.com';

/**
 * Heading shown on the decommission banner. Overridable per environment via
 * `window.LEGACY_DECOMMISSION_TITLE` (see `public/config.js`).
 */
export const getDecommissionTitle = (): string =>
    String(Config.get('LEGACY_DECOMMISSION_TITLE') ?? '') || DEFAULT_DECOMMISSION_TITLE;

/**
 * Customer-facing decommission message. Overridable per environment via
 * `window.LEGACY_DECOMMISSION_MESSAGE` (see `public/config.js`).
 */
export const getDecommissionMessage = (): string =>
    String(Config.get('LEGACY_DECOMMISSION_MESSAGE') ?? '') || DEFAULT_DECOMMISSION_MESSAGE;

/**
 * URL of the new Asset Insights platform behind the "Go to Asset Insights"
 * CTA. Reuses the configured Core Insights portal URL.
 */
export const getAssetInsightsUrl = (): string =>
    String(Config.get('CORE_INSIGHTS_URL') ?? '') || DEFAULT_ASSET_INSIGHTS_URL;

/**
 * Hard override that forces the decommission banner on for every visitor on
 * the login screen regardless of date (set
 * `window.LEGACY_DECOMMISSION_BANNER_ENABLED = 'true'`). When off, the banner
 * shows after a blocked-login redirect (`/auth?decommissioned=true`) or once
 * the scheduled start time below is reached.
 */
export const isDecommissionBannerEnabled = (): boolean =>
    String(Config.get('LEGACY_DECOMMISSION_BANNER_ENABLED') ?? '').toLowerCase() === 'true';

/**
 * Scheduled instant (ISO-8601) from which the banner is shown to every
 * visitor, even before login. Configured via
 * `window.LEGACY_DECOMMISSION_BANNER_START` (see `public/config.js`); returns
 * `null` when unset or unparseable so scheduling is simply disabled.
 */
export const getDecommissionBannerStart = (): Date | null => {
    const raw = String(Config.get('LEGACY_DECOMMISSION_BANNER_START') ?? '').trim();

    if (!raw) {
        return null;
    }

    const start = new Date(raw);

    return Number.isNaN(start.getTime()) ? null : start;
};

/**
 * Whether the decommission banner should be shown on the login screen. True
 * when the user was redirected from a blocked login (`?decommissioned=true`),
 * when the banner is force-enabled, or once the scheduled start time has
 * passed. The date check is client-side, which is acceptable for an
 * informational banner.
 */
export const shouldShowDecommissionBanner = (redirectedFromBlock = false): boolean => {
    if (redirectedFromBlock || isDecommissionBannerEnabled()) {
        return true;
    }

    const start = getDecommissionBannerStart();

    return start !== null && Date.now() >= start.getTime();
};

/**
 * Whether an auth-api error code indicates the legacy platform decommission.
 */
export const isLegacyDecommissionedCode = (code?: string | null): boolean => code === LEGACY_DECOMMISSIONED_CODE;

const fetchWithTimeout = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
        return await fetch(input, {
            ...init,
            signal: controller.signal,
        });
    } finally {
        window.clearTimeout(timeoutId);
    }
};

/**
 * Detects whether the current (already Firebase-authenticated) user has been
 * blocked by the legacy decommission. Used to gate returning users who skip the
 * workspace-selection step (they already hold a workspace cookie).
 *
 * The authoritative decision lives in auth-api: a decommissioned customer gets
 * a 403 with `code: 'legacy_decommissioned'` from `/detail`, while internal
 * Zeitview staff get a normal 200. Fails open (returns `false`) on a transient
 * error so an auth-api outage does not lock out internal users.
 */
export const checkLegacyDecommissioned = async (): Promise<boolean> => {
    if (!AUTH_API_URL) {
        return false;
    }

    try {
        const token = await getAuthToken();

        if (!token) {
            return false;
        }

        const response = await fetchWithTimeout(`${AUTH_API_URL}/detail`, {
            headers: { 'x-user-token': token },
        });

        if (response.status !== 403) {
            return false;
        }

        const body = (await response.json()) as { detail?: { code?: string } | string };
        const code = typeof body?.detail === 'object' && body.detail ? body.detail.code : undefined;

        return isLegacyDecommissionedCode(code);
    } catch {
        return false;
    }
};
